// Phase 19.17 — Upload a Quote or Invoice PDF/image → Gemini Vision extracts
// structured fields → user reviews → backend creates a draft.
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Upload as UploadIcon,
  Loader2,
  FileText,
  Sparkles,
  Trash2,
  CheckCircle2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n) || 0);

const QuoteInvoiceUpload = ({ docType: docTypeProp }) => {
  const params = useParams();
  const navigate = useNavigate();
  const docType = docTypeProp || params.docType || "quote";
  const label = docType === "invoice" ? "Invoice" : "Quote";

  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [warning, setWarning] = useState(null);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setExtracted(null);
    setWarning(null);
  };

  const onExtract = async () => {
    if (!file) { toast.error("Pick a file first"); return; }
    setExtracting(true);
    setWarning(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/doc-ingest/extract`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = res.data;
      setExtracted(data.extracted || {});
      if (!data.ok || data.warning) setWarning(data.warning || "Partial extraction — please review");
      else toast.success("Extraction complete — review below before saving");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to read document");
    } finally {
      setExtracting(false);
    }
  };

  const updateField = (key, value) =>
    setExtracted((prev) => ({ ...(prev || {}), [key]: value }));
  const updateItem = (idx, key, value) =>
    setExtracted((prev) => {
      const next = { ...(prev || {}) };
      const items = [...(next.items || [])];
      items[idx] = { ...items[idx], [key]: value };
      next.items = items;
      return next;
    });
  const removeItem = (idx) =>
    setExtracted((prev) => ({ ...(prev || {}), items: (prev?.items || []).filter((_, i) => i !== idx) }));
  const addItem = () =>
    setExtracted((prev) => ({
      ...(prev || {}),
      items: [...((prev || {}).items || []), { description: "", quantity: 1, unit_price: 0, total_price: 0 }],
    }));

  const onCreate = async () => {
    if (!extracted) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/doc-ingest/create-draft`, {
        document_type: docType,
        extracted,
      });
      const data = res.data;
      toast.success(`${label} draft ${data.quote_number || data.invoice_number} created`);
      if (docType === "quote") navigate(`/quotes/${data.id}`);
      else navigate("/invoices");
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to create ${docType} draft`);
    } finally {
      setCreating(false);
    }
  };

  const items = extracted?.items || [];
  const design = extracted?.design || {};

  return (
    <div className="space-y-6 animate-in" data-testid={`upload-${docType}-page`}>
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
          <UploadIcon className="h-7 w-7 text-primary" /> Upload {label}
        </h1>
        <p className="text-muted-foreground mt-1">
          Drop a PDF or image of an existing {label.toLowerCase()}. We&apos;ll auto-extract the fields with AI — review and save as a draft.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" /> Source document
          </CardTitle>
          <CardDescription>Supported: PDF, PNG, JPG, WEBP (max 15 MB)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            type="file"
            accept="application/pdf,image/png,image/jpeg,image/webp"
            onChange={onPick}
            data-testid={`upload-${docType}-input`}
          />
          {file && (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <FileText className="h-4 w-4" /> {file.name} ({(file.size / 1024).toFixed(1)} KB)
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={onExtract} disabled={!file || extracting} data-testid={`extract-${docType}-btn`}>
              {extracting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {extracting ? "Reading…" : "Extract with AI"}
            </Button>
            {file && (
              <Button variant="outline" onClick={() => { setFile(null); setExtracted(null); setWarning(null); }}>
                Clear
              </Button>
            )}
          </div>
          {warning && (
            <p className="text-sm text-amber-700 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-300 px-3 py-2 rounded">
              {warning}
            </p>
          )}
        </CardContent>
      </Card>

      {extracted && (
        <Card data-testid="extracted-review-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" /> Review extracted data
            </CardTitle>
            <CardDescription>Tweak anything before saving — fields are editable.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>{label} number</Label>
                <Input
                  value={extracted[docType === "invoice" ? "invoice_number" : "quote_number"] || ""}
                  onChange={(e) => updateField(docType === "invoice" ? "invoice_number" : "quote_number", e.target.value)}
                  data-testid="extracted-number"
                />
              </div>
              <div>
                <Label>Client name</Label>
                <Input
                  value={extracted.client_name || ""}
                  onChange={(e) => updateField("client_name", e.target.value)}
                  data-testid="extracted-client-name"
                />
              </div>
              <div>
                <Label>Client email</Label>
                <Input value={extracted.client_email || ""} onChange={(e) => updateField("client_email", e.target.value)} />
              </div>
              <div>
                <Label>Client phone</Label>
                <Input value={extracted.client_phone || ""} onChange={(e) => updateField("client_phone", e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <Label>Property / billing address</Label>
                <Input
                  value={extracted.property_address || extracted.client_address || ""}
                  onChange={(e) => updateField("property_address", e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Label>Description</Label>
                <Textarea rows={2} value={extracted.description || ""} onChange={(e) => updateField("description", e.target.value)} />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Line items</Label>
                <Button size="sm" variant="outline" onClick={addItem} data-testid="extracted-add-item">+ Add line</Button>
              </div>
              {items.length === 0 && (
                <p className="text-sm text-muted-foreground">No line items detected. Add at least one if needed.</p>
              )}
              <div className="space-y-2">
                {items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-6" placeholder="Description"
                      value={it.description || ""}
                      onChange={(e) => updateItem(idx, "description", e.target.value)} />
                    <Input className="col-span-1" type="number" step="any" placeholder="Qty"
                      value={it.quantity ?? 1}
                      onChange={(e) => updateItem(idx, "quantity", parseFloat(e.target.value) || 0)} />
                    <Input className="col-span-2" type="number" step="0.01" placeholder="Unit $"
                      value={it.unit_price ?? 0}
                      onChange={(e) => updateItem(idx, "unit_price", parseFloat(e.target.value) || 0)} />
                    <Input className="col-span-2" type="number" step="0.01" placeholder="Total $"
                      value={it.total_price ?? 0}
                      onChange={(e) => updateItem(idx, "total_price", parseFloat(e.target.value) || 0)} />
                    <Button size="icon" variant="ghost" onClick={() => removeItem(idx)}
                      className="col-span-1 text-destructive"
                      data-testid={`extracted-remove-item-${idx}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Subtotal</Label>
                <Input type="number" step="0.01"
                  value={extracted.subtotal ?? 0}
                  onChange={(e) => updateField("subtotal", parseFloat(e.target.value) || 0)} />
              </div>
              <div>
                <Label>Tax rate %</Label>
                <Input type="number" step="0.01"
                  value={extracted.tax_rate ?? 0}
                  onChange={(e) => updateField("tax_rate", parseFloat(e.target.value) || 0)} />
              </div>
              <div>
                <Label>Total</Label>
                <Input type="number" step="0.01"
                  value={extracted.total_amount ?? 0}
                  onChange={(e) => updateField("total_amount", parseFloat(e.target.value) || 0)} />
              </div>
            </div>

            {(design.logo_present || design.primary_color_hex) && (
              <div className="rounded border border-dashed bg-muted/30 p-3 text-sm space-y-1">
                <p className="font-medium flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" /> Design hints from the original
                </p>
                {design.logo_present && (
                  <p>• Logo: <span className="text-muted-foreground">{design.logo_description || "detected"}</span> ({design.logo_position})</p>
                )}
                {design.primary_color_hex && (
                  <p>• Primary color: <span className="inline-block w-3 h-3 align-middle rounded-sm border" style={{ background: design.primary_color_hex }} /> {design.primary_color_hex}</p>
                )}
                {design.header_layout && (<p>• Layout: {design.header_layout}</p>)}
                <p className="text-xs text-muted-foreground pt-1">
                  These will be used as defaults when you switch to the live editor (Phase 19.18).
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setExtracted(null); setWarning(null); }}>Cancel</Button>
              <Button onClick={onCreate} disabled={creating} data-testid={`save-${docType}-draft-btn`}>
                {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save as Draft {label}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {extracted && (
        <div className="flex items-center justify-end gap-4 text-sm text-muted-foreground">
          Computed item subtotal:&nbsp;
          <span className="font-mono">{formatCurrency(items.reduce((s, it) => s + (Number(it.total_price) || 0), 0))}</span>
        </div>
      )}
    </div>
  );
};

export default QuoteInvoiceUpload;
